/**
 * 登录页面组件
 * Login Page Component
 * 设计理念: 工程美学分屏布局，左侧品牌展示，右侧聚焦表单
 */
import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, message, Tabs, Typography, Space } from 'antd';
import { UserOutlined, LockOutlined, ProjectOutlined, TeamOutlined, SafetyOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useResponsiveConfig } from '../../responsive/hooks/useResponsiveConfig';
import './index.css';

const { Title, Text } = Typography;

interface LoginFormData {
  username: string;
  password: string;
}

interface RegisterFormData {
  username: string;
  password: string;
  confirmPassword: string;
  realName: string;
  phone: string;
  email?: string;
}

const LoginPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('login');
  const [loginForm] = Form.useForm<LoginFormData>();
  const [registerForm] = Form.useForm<RegisterFormData>();

  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, loading, error, clearError, isAuthenticated } = useAuthStore();
  const { isMobile, isTablet } = useResponsiveConfig();

  useEffect(() => {
    const isPreview = new URLSearchParams(location.search).has('preview');
    if (isAuthenticated && !isPreview) {
      const from = (location.state as any)?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  useEffect(() => {
    if (error) {
      message.error(error);
      clearError();
    }
  }, [error, clearError]);

  const handleLogin = async (values: LoginFormData) => {
    const result = await login(values);
    if (result.success) {
      message.success(result.message);
      const from = (location.state as any)?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    } else {
      message.error(result.message);
    }
  };

  const handleRegister = async (values: RegisterFormData) => {
    const registerData = {
      username: values.phone,
      phone: values.phone,
      realName: values.realName,
      password: values.password,
      confirmPassword: values.confirmPassword,
      email: values.email
    };
    const result = await register(registerData);
    if (result.success) {
      message.success(result.message);
      const from = (location.state as any)?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    } else {
      message.error(result.message);
    }
  };

  return (
    <div className="login-page">
      <div className="login-background">
        {/* 左侧品牌区域 */}
        <div className="login-brand">
          <div className="brand-content">
            <div className="brand-logo">
              <ProjectOutlined />
            </div>
            <h1 className="brand-title">斌宇林建</h1>
            <p className="brand-subtitle">项目管理系统</p>
            <div className="brand-divider"></div>
            <p className="brand-description">
              专注工程项目全生命周期管理<br />
              让每一项工作都可追溯、可协作、可掌控
            </p>
            <div className="brand-features">
              <div className="feature-item">
                <TeamOutlined className="feature-icon" />
                <span>团队协作</span>
              </div>
              <div className="feature-item">
                <SafetyOutlined className="feature-icon" />
                <span>安全可控</span>
              </div>
            </div>
          </div>
          {/* 装饰性几何图形 */}
          <div className="brand-decoration">
            <div className="deco-circle deco-circle-1"></div>
            <div className="deco-circle deco-circle-2"></div>
            <div className="deco-line deco-line-1"></div>
            <div className="deco-line deco-line-2"></div>
          </div>
        </div>

        {/* 右侧表单区域 */}
        <div className="login-form-area">
          <Card className="login-card" variant="borderless">
            <div className="login-header">
              <Title level={isMobile ? 3 : 2} className="login-title">
                {activeTab === 'login' ? '欢迎回来' : '创建账户'}
              </Title>
              <Text className="login-subtitle">
                {activeTab === 'login' ? '请登录您的账户以继续' : '填写以下信息完成注册'}
              </Text>
            </div>

            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              centered
              className="login-tabs"
              size={isMobile ? 'large' : 'middle'}
              items={[
                {
                  key: 'login',
                  label: '登录',
                  children: (
                    <Form
                      form={loginForm}
                      name="login"
                      onFinish={handleLogin}
                      className="login-form"
                    >
                      <Form.Item
                        name="username"
                        rules={[
                          { required: true, message: '请输入用户名' },
                          { min: 3, message: '用户名至少3个字符' },
                          { max: 30, message: '用户名不能超过30个字符' },
                          { pattern: /^[a-zA-Z0-9_]+$/, message: '用户名只能包含字母、数字和下划线' }
                        ]}
                      >
                        <Input
                          prefix={<UserOutlined />}
                          placeholder="用户名"
                          autoComplete="username"
                          size="large"
                        />
                      </Form.Item>

                      <Form.Item
                        name="password"
                        rules={[
                          { required: true, message: '请输入密码' }
                        ]}
                      >
                        <Input.Password
                          prefix={<LockOutlined />}
                          placeholder="密码"
                          autoComplete="current-password"
                          size="large"
                        />
                      </Form.Item>

                      <div className="login-tips">
                        <Text type="secondary">
                          仅斌宇林建内部使用
                        </Text>
                      </div>

                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        className="login-button"
                        block
                      >
                        登 录
                      </Button>
                    </Form>
                  ),
                },
                {
                  key: 'register',
                  label: '注册',
                  children: (
                    <Form
                      form={registerForm}
                      name="register"
                      onFinish={handleRegister}
                      className="register-form"
                    >
                      <Form.Item
                        name="realName"
                        rules={[
                          { required: true, message: '请输入真实姓名' },
                          { max: 50, message: '姓名不能超过50个字符' }
                        ]}
                      >
                        <Input
                          placeholder="真实姓名"
                          autoComplete="name"
                          size="large"
                        />
                      </Form.Item>

                      <Form.Item
                        name="phone"
                        rules={[
                          { required: true, message: '请输入手机号码' },
                          { pattern: /^1[3-9]\d{9}$/, message: '请输入有效的手机号码' }
                        ]}
                      >
                        <Input
                          prefix={<UserOutlined />}
                          placeholder="手机号码（将作为登录账号）"
                          autoComplete="tel"
                          size="large"
                        />
                      </Form.Item>

                      <Form.Item
                        name="email"
                        rules={[
                          { type: 'email', message: '请输入有效的邮箱地址' }
                        ]}
                      >
                        <Input
                          prefix={<span className="custom-icon">@</span>}
                          placeholder="邮箱地址（可选）"
                          autoComplete="email"
                          size="large"
                        />
                      </Form.Item>

                      <Form.Item
                        name="password"
                        rules={[
                          { required: true, message: '请输入密码' },
                          { min: 6, message: '密码至少6个字符' },
                          { max: 128, message: '密码不能超过128个字符' }
                        ]}
                      >
                        <Input.Password
                          prefix={<LockOutlined />}
                          placeholder="密码（至少6位）"
                          autoComplete="new-password"
                          size="large"
                        />
                      </Form.Item>

                      <Form.Item
                        name="confirmPassword"
                        dependencies={['password']}
                        rules={[
                          { required: true, message: '请确认密码' },
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              if (!value || getFieldValue('password') === value) {
                                return Promise.resolve();
                              }
                              return Promise.reject(new Error('两次输入的密码不一致'));
                            },
                          }),
                        ]}
                      >
                        <Input.Password
                          prefix={<LockOutlined />}
                          placeholder="确认密码"
                          autoComplete="new-password"
                          size="large"
                        />
                      </Form.Item>

                      <div className="register-tips">
                        <Text type="secondary">
                          注册后将获得基础查看权限，如需更多权限请联系管理员
                        </Text>
                      </div>

                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        className="register-button"
                        block
                      >
                        注 册
                      </Button>
                    </Form>
                  ),
                },
              ]}
            />
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
